import React, { useMemo } from 'react';
import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Banner = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'imageSrc',
})(({ theme, imageSrc }) => ({
    position: 'relative',
    width: '100%',
    minHeight: "340px",
    backgroundImage: `url(${imageSrc})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    overflow: 'hidden',
    // Dark overlay for readability
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 40%, rgba(0, 0, 0, 0.3) 70%, transparent 100%)',
        zIndex: 1,
    },
    [theme.breakpoints.down('md')]: {
        minHeight: "220px",
        backgroundPosition: 'center center',
        '&::before': {
            background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.80) 0%, rgba(0, 0, 0, 0.6) 100%)',
        },
    },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    zIndex: 2,
    padding: theme.spacing(5, 6),
    maxWidth: '700px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(3, 2.5),
        maxWidth: '100%',
        textAlign: 'center',
        alignItems: 'center',
    },
}));

const MainHeading = styled(Typography)(({ theme }) => ({
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '3.5rem',
    lineHeight: 1.1,
    letterSpacing: '-1px',
    marginBottom: theme.spacing(1.5),
    animation: `${fadeInUp} 0.7s ease-out`,
    textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    whiteSpace: 'nowrap',
    textDecoration: 'underline',
    textDecorationColor: '#ffffff',
    textDecorationThickness: '2px',
    textUnderlineOffset: '8px',
    '& span': {
        display: 'block',
        color: '#dc3545',
        whiteSpace: 'nowrap',
        textDecoration: 'underline',
        textDecorationColor: '#ffffff',
        textDecorationThickness: '2px',
        textUnderlineOffset: '8px',
    },
    [theme.breakpoints.down('md')]: {
        fontSize: '2rem',
        marginBottom: theme.spacing(1),
        whiteSpace: 'normal',
        textDecorationThickness: '1.5px',
        textUnderlineOffset: '6px',
        '& span': {
            whiteSpace: 'normal',
            textDecorationThickness: '1.5px',
            textUnderlineOffset: '6px',
        },
    },
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.6rem',
        textDecorationThickness: '1px',
        textUnderlineOffset: '4px',
        '& span': {
            textDecorationThickness: '1px',
            textUnderlineOffset: '4px',
        },
    },
}));

const SubHeading = styled(Typography)(({ theme }) => ({
    color: 'rgba(255, 255, 255, 0.85)',
    fontSize: '1.1rem',
    fontWeight: 400,
    lineHeight: 1.6,
    animation: `${fadeInUp} 0.7s ease-out 0.15s both`,
    textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
    alignSelf: 'center',
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
        fontSize: '0.875rem',
    },
}));

export const TopBanner = ({ altText }) => {
    const lngsltd = useMemo(() => JSON.parse(localStorage.getItem('languageSelected')), []);
    
    // Get localized text with fallbacks
    const headingLine1 = lngsltd?.["Built for Skeidar."] || "Built for Skeidar.";
    const headingLine2 = lngsltd?.["Built for you."] || "Built for you.";
    const subtitle = lngsltd?.["Your platform for learning and development"] || "Your platform for learning and development";

    return (
        <Banner imageSrc={`${process.env.PUBLIC_URL}/images/dashboardBanner2.png`} aria-label={altText}>
            <ContentContainer>
                <MainHeading variant="h1">
                    {headingLine1}
                    <span>{headingLine2}</span>
                </MainHeading>
                <SubHeading>
                    {subtitle}
                </SubHeading>
            </ContentContainer>
        </Banner>
    );
};
